import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { Redirect } from 'react-router-dom'
import ModulePanel from '../../../../ui/ModulePanel/ModulePanel'
import Button from '../../../../ui/Button/Button'
import StatusMessage, { useStatusMessage } from '../../../../ui/StatusMessage/StatusMessage'
import { AuthContext } from '../../../../App'
import { isSmsAllowedUser } from '../../../../config/smsAccessControl'
import listSmsMessages from '../../../../api/sms/listSmsMessages'
import SmsBalance from './SmsBalance'
import SmsFilters from './SmsFilters'
import SmsTable from './SmsTable'
import SmsDetailPopUp from './SmsDetailPopUp'
import SmsComposePopUp from './SmsComposePopUp'
import { parseUaDateToMs } from './smsUiUtils'
import {
  DEFAULT_SMS_LIST_LIMIT,
  createSmsJournalPoller,
  hasIntermediateSmsStatuses,
  resolveSmsPollLimit,
} from './smsStatusPolling'
import './Sms.sass'

const EMPTY_FILTERS = {
  source: '',
  submissionStatus: '',
  deliveryStatus: '',
  createdFromDate: '',
  createdToDate: '',
}

const buildListPayload = (filters, { cursor = null, limit = DEFAULT_SMS_LIST_LIMIT } = {}) => {
  const payload = { limit }
  if (filters.source) payload.source = filters.source
  if (filters.submissionStatus) payload.submissionStatus = filters.submissionStatus
  if (filters.deliveryStatus) payload.deliveryStatus = filters.deliveryStatus
  const from = parseUaDateToMs(filters.createdFromDate, { endOfDay: false })
  const to = parseUaDateToMs(filters.createdToDate, { endOfDay: true })
  if (from != null) payload.createdFrom = from
  if (to != null) payload.createdTo = to
  if (cursor != null) payload.cursor = cursor
  return payload
}

const rowsContentKey = (items = []) =>
  items
    .map(
      (item) =>
        `${item.id}:${item.submissionStatus}:${item.deliveryStatus}:${item.updatedAt ?? ''}:${item.normalizedError ?? ''}`
    )
    .join('|')

function Sms() {
  const { user } = useContext(AuthContext)
  const allowed = isSmsAllowedUser(user?.uid)
  const [statusMessage, setStatusMessage] = useStatusMessage()
  const [filters, setFilters] = useState(EMPTY_FILTERS)
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS)
  const [rows, setRows] = useState([])
  const [nextCursor, setNextCursor] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [detailId, setDetailId] = useState(null)
  const [composeOpen, setComposeOpen] = useState(false)
  const [filterFormKey, setFilterFormKey] = useState(0)

  const rowsRef = useRef(rows)
  const appliedFiltersRef = useRef(appliedFilters)
  const requestIdRef = useRef(0)
  const pollerRef = useRef(null)
  const mountedRef = useRef(true)

  rowsRef.current = rows
  appliedFiltersRef.current = appliedFilters

  const loadPage = useCallback(
    async ({
      append = false,
      cursor = null,
      nextFilters = appliedFiltersRef.current,
      mode = 'initial',
    } = {}) => {
      const requestId = ++requestIdRef.current
      const isBackground = mode === 'background'

      if (append) setIsLoadingMore(true)
      else if (!isBackground) setIsLoading(true)

      const limit = isBackground
        ? resolveSmsPollLimit(rowsRef.current.length)
        : DEFAULT_SMS_LIST_LIMIT

      const result = await listSmsMessages(
        buildListPayload(nextFilters, {
          cursor: append ? cursor : null,
          limit,
        })
      )

      if (!mountedRef.current || requestId !== requestIdRef.current) {
        return { ok: false, stale: true }
      }

      if (!result.ok) {
        if (!isBackground) {
          setStatusMessage({
            show: true,
            type: 'fail',
            message: result.message || 'Помилка завантаження SMS',
            closeAfter: 7000,
          })
        }
        if (append) setIsLoadingMore(false)
        else if (!isBackground) setIsLoading(false)
        return { ok: false, stale: false, message: result.message }
      }

      const items = Array.isArray(result.data?.items) ? result.data.items : []
      const nextCursorValue = result.data?.nextCursor ?? null

      if (append) {
        setRows((prev) => [...prev, ...items])
        setNextCursor(nextCursorValue)
        setIsLoadingMore(false)
      } else {
        setRows((prev) => (rowsContentKey(prev) === rowsContentKey(items) ? prev : items))
        setNextCursor((prev) => (prev === nextCursorValue ? prev : nextCursorValue))
        if (!isBackground) setIsLoading(false)
      }

      return { ok: true, stale: false, items }
    },
    [setStatusMessage]
  )

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  // Initial / filter-driven load (full loader). Invalidates in-flight poll responses.
  useEffect(() => {
    if (!allowed) return undefined
    requestIdRef.current += 1
    if (pollerRef.current) {
      pollerRef.current.invalidate()
    }
    loadPage({ append: false, cursor: null, nextFilters: appliedFilters, mode: 'initial' })
    return undefined
  }, [allowed, appliedFilters, loadPage])

  const needsPolling = hasIntermediateSmsStatuses(rows)

  // Sequential background polling while visible intermediate statuses remain.
  useEffect(() => {
    if (!allowed || !needsPolling) {
      if (pollerRef.current) {
        pollerRef.current.stop()
        pollerRef.current = null
      }
      return undefined
    }

    const poller = createSmsJournalPoller({
      fetchPage: async () => {
        await loadPage({
          append: false,
          cursor: null,
          nextFilters: appliedFiltersRef.current,
          mode: 'background',
        })
      },
      shouldContinue: () => hasIntermediateSmsStatuses(rowsRef.current),
      isPageVisible: () => typeof document === 'undefined' || !document.hidden,
    })
    pollerRef.current = poller
    poller.start()

    const onVisibility = () => {
      if (document.hidden) {
        poller.pauseForHidden()
        return
      }
      poller.resumeFromVisible()
    }
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      document.removeEventListener('visibilitychange', onVisibility)
      poller.stop()
      if (pollerRef.current === poller) {
        pollerRef.current = null
      }
    }
  }, [allowed, needsPolling, loadPage])

  if (!allowed) {
    return <Redirect to="/settings/pages" />
  }

  const applyFilters = () => {
    setAppliedFilters(filters)
  }

  const resetFilters = () => {
    setFilters(EMPTY_FILTERS)
    setAppliedFilters(EMPTY_FILTERS)
    setFilterFormKey((key) => key + 1)
  }

  return (
    <div className="SmsPage">
      {statusMessage.show ? (
        <StatusMessage className="Site-StatusMessage" type={statusMessage.type} message={statusMessage.message} />
      ) : null}
      <ModulePanel
        title="SMS"
        buttons={
          <Button
            title="Нове SMS"
            theme="solid"
            fill="accent"
            size={32}
            onClick={() => setComposeOpen(true)}
          />
        }
      >
        <SmsBalance />
        <SmsFilters
          filters={filters}
          onChange={setFilters}
          onApply={applyFilters}
          onReset={resetFilters}
          formKey={filterFormKey}
        />
        <SmsTable
          rows={rows}
          isLoading={isLoading}
          nextCursor={nextCursor}
          isLoadingMore={isLoadingMore}
          onOpenDetail={setDetailId}
          onLoadMore={() => loadPage({ append: true, cursor: nextCursor, mode: 'append' })}
        />
      </ModulePanel>

      {detailId ? <SmsDetailPopUp smsMessageId={detailId} onClose={() => setDetailId(null)} /> : null}
      <SmsComposePopUp
        show={composeOpen}
        onClose={() => setComposeOpen(false)}
        onSuccess={() => {
          setStatusMessage({
            show: true,
            type: 'success',
            message: 'SMS прийнято до відправки',
            closeAfter: 5000,
          })
          loadPage({ append: false, cursor: null, mode: 'initial' })
        }}
      />
    </div>
  )
}

export default Sms
